import { suggestedMentionTargets, suggestedMentionWords } from "@repo/shared/qg-next/mention-words";
import type { SuggestedMentionDto } from "@repo/shared/qg-next/types";
import { Fragment } from "react";
import { useOptionalMentionInteraction } from "@/features/rich-text/mentions";

/**
 * What a section's open mention findings ask of it, one line per frame, each suggested element
 * by name as a link to it: "Names Promotion return without a mention", "Should mention Retail
 * POS". Only live elements are suggested, so a mention whose candidates are all gone draws nothing;
 * a required mention that names no element names the types it reaches.
 */
export function QgNextSuggestedMentions({ mentions }: { mentions?: SuggestedMentionDto[] }) {
  const interaction = useOptionalMentionInteraction();
  const shown = (mentions ?? []).filter((mention) => suggestedMentionTargets(mention).length > 0);
  if (shown.length === 0) return null;
  return (
    <div data-qg-next-suggested-mentions="true" className="mt-1.5 space-y-0.5">
      {shown.map((mention) => {
        const targets = suggestedMentionTargets(mention);
        const { before, after } = suggestedMentionWords(mention.frame);
        const linked = new Map(mention.elements.map((element) => [element.name, element]));
        const parts = targets.map((target) => {
          const element = linked.get(target);
          return element && interaction ? (
            <button
              key={`element:${element.entityId}`}
              type="button"
              data-qg-next-suggested-element={element.entityId}
              className="font-medium text-foreground underline decoration-dotted underline-offset-2 hover:text-primary"
              onClick={() =>
                void interaction.activateMention({
                  id: element.entityId,
                  mentionType: element.entityTypeKey,
                })
              }
            >
              {element.name}
            </button>
          ) : (
            <span key={`name:${target}`} className="font-medium text-foreground">
              {target}
            </span>
          );
        });
        return (
          <p
            key={mention.frame}
            data-qg-next-suggested-mention={mention.frame}
            className="text-xs text-muted-foreground"
          >
            {before}
            {parts.map((part, index) => (
              <Fragment key={part.key ?? index}>
                {index === 0 ? "" : index === parts.length - 1 ? " and " : ", "}
                {part}
              </Fragment>
            ))}
            {after}.
          </p>
        );
      })}
    </div>
  );
}
